import ImageSmallIcon from '@/widgets/UploadFilesWidgets/components/ImageSmallIcon';
import { Column, ColumnDef, Table } from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';

import { FileUploadDTO } from '@/types/api-types';
import {
  ExplorerItem,
  NormalizedAgreement,
  NormalizedFile,
} from '@/types/file-explorer-types';

import IconChevronDown from '@/assets/svg/icon-desc-table.svg?react';
import IconFolder from '@/assets/svg/icon-folder.svg?react';
import IconImage from '@/assets/svg/icon-img.svg?react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useFiltersStore } from '@/stores/filtersStore';
import { useFiltersUploadStore } from '@/stores/filtersUploadStore';

import {
  COLUMN_NAMES,
  ColumnNamesType,
  ExplorerItemType,
} from '@/constants/table-columns-config';

import { FilterWidgetComponent } from './FilterWidgetComponent';
import { FilterWidgetUploadComponent } from './FilterWidgetUploadComponent';

interface ColumnHeaderAgreementProps {
  column: Column<NormalizedAgreement>;
  currentPage: 'search' | 'dashboard';
  openFilterColumn: string | null;
  setOpenFilterColumn: (columnId: string | null) => void;
}

interface ColumnHeaderUploadFilesProps {
  label: string;
  column: Column<FileUploadDTO>;
  openFilterColumn: string | null;
  setOpenFilterColumn: (columnId: string | null) => void;
  table: Table<FileUploadDTO>;
  disabledOptions?: boolean;
}
interface ColumnHeaderDefaultProps {
  column: Column<ExplorerItem>;
  entityType: ExplorerItemType;
}

export const ColumnHeaderAgreement = ({
  column,
  currentPage,
  openFilterColumn,
  setOpenFilterColumn,
}: ColumnHeaderAgreementProps) => {
  const { setSorting, sorting } = useFileExplorerStore();
  const { setPagination, pagination } = useFiltersStore();
  const columnKey = column.id;

  return (
    <div className="relative">
      <DropdownMenu
        open={openFilterColumn === columnKey}
        onOpenChange={(open) => setOpenFilterColumn(open ? columnKey : null)}
      >
        <DropdownMenuTrigger asChild>
          <div className="flex cursor-pointer items-center gap-2 font-bold hover:bg-transparent sm:font-semibold">
            {
              COLUMN_NAMES(ExplorerItemType.AGREEMENT)[
                columnKey as ColumnNamesType
              ]
            }
            <div className="flex items-center">
              {!column.getIsSorted() && (
                <IconChevronDown className="h-2 w-2 fill-primary" />
              )}
              {column.getIsSorted() === 'asc' ? (
                <IconChevronDown className="h-2 w-2 rotate-180 fill-primary" />
              ) : column.getIsSorted() === 'desc' ? (
                <IconChevronDown className="h-2 w-2 fill-primary" />
              ) : null}
            </div>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[310px] p-0">
          <FilterWidgetComponent
            currentPage={currentPage}
            column={column}
            sortBy={
              typeof sorting.find((sort) => sort.id === columnKey) === 'object'
                ? sorting.find((sort) => sort.id === columnKey)?.desc
                  ? 'desc'
                  : 'asc'
                : undefined
            }
            onSortChange={(sortBy) => {
              setSorting([{ id: columnKey, desc: sortBy === 'desc' }]);
              setOpenFilterColumn(null);
            }}
            onClear={() => {
              setSorting([]);
              setOpenFilterColumn(null);
            }}
            onApply={() => {
              setOpenFilterColumn(null);
              setPagination({
                pageIndex: 0,
                pageSize: pagination?.pageSize ?? 11,
              });
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const ColumnHeaderUploadFiles = ({
  table,
  label,
  column,
  openFilterColumn,
  setOpenFilterColumn,
  disabledOptions,
}: ColumnHeaderUploadFilesProps) => {
  const columnKey = column.id;
  const { columnSortState, setColumnSortState, setSorting } =
    useFiltersUploadStore();

  return (
    <div className="relative">
      <DropdownMenu
        open={openFilterColumn === columnKey}
        onOpenChange={(open) => setOpenFilterColumn(open ? columnKey : null)}
      >
        <DropdownMenuTrigger asChild>
          <div className="flex cursor-pointer items-center gap-2 font-bold hover:bg-transparent sm:font-semibold">
            {label}
            <div className="flex items-center">
              {!column.getIsSorted() && (
                <IconChevronDown className="h-2 w-2 fill-primary" />
              )}
              {column.getIsSorted() === 'asc' ? (
                <IconChevronDown className="h-2 w-2 rotate-180 fill-primary" />
              ) : column.getIsSorted() === 'desc' ? (
                <IconChevronDown className="h-2 w-2 fill-primary" />
              ) : null}
            </div>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[310px] p-0">
          <FilterWidgetUploadComponent
            label={label}
            table={table}
            column={column}
            sortBy={columnSortState[columnKey] || 'asc'}
            onSortChange={(sortBy) => {
              const newSortState = { ...columnSortState };
              if (sortBy === columnSortState[columnKey]) {
                delete newSortState[columnKey];
                setSorting([]);
              } else {
                newSortState[columnKey] = sortBy;
                setSorting([{ id: columnKey, desc: sortBy === 'desc' }]);
              }
              setColumnSortState(newSortState);
              setOpenFilterColumn(null);
            }}
            onClear={() => {
              const newSortState = { ...columnSortState };
              delete newSortState[columnKey];
              setColumnSortState(newSortState);
              setSorting([]);
              setOpenFilterColumn(null);
            }}
            onApply={() => {
              setOpenFilterColumn(null);
            }}
            disabledOptions={disabledOptions}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const ColumnHeaderDefault = ({
  column,
  entityType,
}: ColumnHeaderDefaultProps) => {
  const columnKey = column.id;
  return (
    <div className="flex items-center gap-2 font-semibold">
      {COLUMN_NAMES(entityType)[columnKey as ColumnNamesType]}
    </div>
  );
};

export const getSmallColumns = <T extends ExplorerItem>(
  entityType: ExplorerItemType,
  isMoveFile: boolean,
  fileUuidToMove: string | null,
  updateFileAfterMove: (
    file_uuid: string,
    agreement_uuid: string,
  ) => Promise<void>,
  setFileUuidToMove: (uuid: string | null) => void,
  setIsMoveFileDialogOpen: (isOpen: boolean) => void,
  setCurrentUUID: (uuid: string) => void,
  setCurrentEntityType: (type: ExplorerItemType) => void,
  handleNavigate: (uuid: string, type: ExplorerItemType) => void,
): ColumnDef<T>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: () => (
      <span className="flex w-full items-center gap-3 font-semibold hover:bg-transparent">
        Назва
      </span>
    ),
    cell: ({ row }) => (
      <div className="flex w-full items-center gap-3">
        {entityType === ExplorerItemType.FILE ? (
          (row.original as NormalizedFile).thumbnail_url ? (
            <ImageSmallIcon
              fileLink={(row.original as NormalizedFile).thumbnail_url!}
            />
          ) : (
            <IconImage className="h-[22px] min-w-[30px] fill-dark-red" />
          )
        ) : (
          <IconFolder className="h-[22px] min-w-[30px]" />
        )}
        <span className="text-base font-medium">{row.original.name}</span>
      </div>
    ),
  },
  ...(isMoveFile && entityType === ExplorerItemType.AGREEMENT
    ? [
        {
          id: 'actions',
          cell: ({ row }: { row: { original: T } }) => (
            <div className="flex justify-end">
              <Button
                variant="outline"
                className="h-[24px] w-[124px]"
                onClick={async () => {
                  if (!fileUuidToMove) return;
                  await updateFileAfterMove(fileUuidToMove, row.original.uuid);
                  setFileUuidToMove(null);
                  setIsMoveFileDialogOpen(false);
                  setCurrentUUID(row.original.uuid);
                  setCurrentEntityType(ExplorerItemType.FILE);
                  handleNavigate(row.original.uuid, ExplorerItemType.FILE);
                }}
              >
                Перемістити
              </Button>
            </div>
          ),
        } as ColumnDef<T>,
      ]
    : []),
  ...(entityType === ExplorerItemType.FILE
    ? [
        {
          accessorKey: 'size',
          header: ({ column }: { column: Column<T> }) => (
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === 'asc')
              }
              className="font-semibold hover:bg-transparent"
            >
              Розмір
              {column.getIsSorted() === 'asc' ? (
                <IconChevronDown className="ml-2 h-4 w-4 fill-primary" />
              ) : column.getIsSorted() === 'desc' ? (
                <IconChevronDown className="ml-2 h-4 w-4 fill-primary" />
              ) : null}
            </Button>
          ),
        } as ColumnDef<T>,
      ]
    : []),
];

interface ActionsColumnProps {
  setIsDetailsPanelOpen: (isOpen: boolean) => void;
  setSelectedRow: (row: NormalizedAgreement | null) => void;
}

export const getActionsColumn = ({
  setIsDetailsPanelOpen,
  setSelectedRow,
}: ActionsColumnProps): ColumnDef<NormalizedAgreement> => ({
  id: 'actions',
  header: () => <div className="text-right font-semibold">Дія</div>,
  size: 40,
  cell: ({ row }) => (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsDetailsPanelOpen(true);
              setSelectedRow(row.original);
            }}
          >
            Інформація про документ
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  ),
});
